import { CONSULTATION_STEPS, CONSULTATION_TYPE, MODULE_TYPE } from '@abiglobalhealth/go-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getIndicationSurveyModule } from '@/api/indications'
import { getAssessmentModule } from '@/api/surveys'
import { Button } from '@/components/Button'
import { useConsultationStep } from '@/hooks/useConsultationStep'
import { useIsConsultationType } from '@/hooks/useIsConsultationType'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useSurveyCategories } from '@/hooks/useSurveyCategories'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { getEmergencyNumberTranslation } from '@/utils/emergencyNumber'
import { getInitialStep } from '@/utils/step'

import { AutocompletedSearch } from './AutocompletedSearch'
import { IndicationsGrid } from './IndicationsGrid'

export const IndicationCategories = () => {
  const { t } = useTranslation()
  const { surveyCategories, updateIndicationCategory } = useSurveyCategories()

  const { showLoading, hideLoading } = useLoading()
  const { currentModule, updateModule } = useModule()
  const { updateStep } = useStep()
  const { currentConsultationStep, updateStep: updateConsultationStep } = useConsultationStep()
  const { user } = useUser()
  const isPrescription = useIsConsultationType(CONSULTATION_TYPE.PRESCRIPTION)
  const isMental = useIsConsultationType(CONSULTATION_TYPE.MENTAL)

  const { addTrace } = useTraces()
  const [selectedIndication, setSelectedIndication] = useState<number | null>(null)

  if (currentModule?.type !== MODULE_TYPE.INDICATION_CATEGORIES && currentModule?.type !== MODULE_TYPE.ASSESSMENT_SURVEY_CATEGORIES) {
    throw new Error('IndicationCategories component must be used within an INDICATION_CATEGORIES module')
  }

  const getSurvey = async (indicationSurveyId: number) => {
    showLoading()

    try {
      const moduleConsultationType = currentModule.consultationType
      const response = isPrescription
        ? await getIndicationSurveyModule({
            consultationType: moduleConsultationType,
            indicationSurveyId,
            uniqueId: user?.data.uniqueId!
          })
        : await getAssessmentModule({
            consultationType: CONSULTATION_TYPE.MENTAL,
            assessmentSurveyId: indicationSurveyId,
            uniqueId: user?.data.uniqueId!
          })

      updateModule(response)
      const indicationCategory = surveyCategories?.find((sc) => sc.options.some((option) => option.id === indicationSurveyId))
      updateIndicationCategory(indicationCategory!)
      const initialStep = getInitialStep({ module: response, user: user! })
      updateStep(initialStep)
      addTrace(response.id, initialStep.id)
    } finally {
      hideLoading()
    }
  }

  const handleSelectedIndication = (indicationSurveyId: number) => {
    if (isPrescription) return getSurvey(indicationSurveyId)

    if (isMental) {
      setSelectedIndication(indicationSurveyId)
      updateConsultationStep(CONSULTATION_STEPS.MENTAL_HEALTH_DISCLAIMER)
    }
  }

  useEffect(() => {
    if (currentConsultationStep !== CONSULTATION_STEPS.MENTAL_HEALTH_DISCLAIMER) {
      setSelectedIndication(null)
    }
  }, [currentConsultationStep])

  if (!surveyCategories?.length) return null

  if (selectedIndication) {
    return (
      <div className="flex flex-col gap-6 p-4">
        <p>
          <b>{t('general.consultation.mentalHealthEvaluation.disclaimer.please_read')}</b>
          <ul className="mt-2 mb-4 list-disc pl-5">
            <li
              dangerouslySetInnerHTML={{
                __html: getEmergencyNumberTranslation(user?.data.country!)
              }}
            />
            <li>{t('general.consultation.mentalHealthEvaluation.disclaimer.severe')}</li>
          </ul>
        </p>
        <Button onClick={() => getSurvey(selectedIndication)}>{t('button.understand_agree')}</Button>
        <p className="text-sm">{t('general.consultation.mentalHealthEvaluation.disclaimer.severe_examples')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col rounded-lg p-4" data-testid="indication-categories">
      {isPrescription && (
        <AutocompletedSearch
          onChange={(option) => {
            if (!option) return
            handleSelectedIndication(option.value.id)
          }}
        />
      )}

      <IndicationsGrid onChange={handleSelectedIndication} />
    </div>
  )
}
